import { StatusListType } from "./LeaveStatusList";
import { useDispatch, useSelector } from "react-redux";
import { withdrawLeave, cancelLeave } from "../../store/slices/leaveSlice";
import { RootDispatch, RootState } from "../../store";
import React, { useState } from "react";

interface LeaveCardProps {
  application: any;
  type: StatusListType;
  fromHomeTab?: boolean;
}

const LeaveCard: React.FC<LeaveCardProps> = ({
  application,
  type,
  fromHomeTab,
}) => {
  const dispatch = useDispatch<RootDispatch>();
  const [showConfirm, setShowConfirm] = useState(false);
  const [actionType, setActionType] = useState<"withdraw" | "cancel" | null>(
    null
  );

  const isWithdrawVisible =
    type === "pending" &&
    !fromHomeTab &&
    application.status !== "cancel_pending";
  const isCancelVisible = application.status === "approved" && !fromHomeTab;

  const handleWithdrawClick = () => {
    setActionType("withdraw");
    setShowConfirm(true);
  };

  const handleCancelClick = () => {
    setActionType("cancel");
    setShowConfirm(true);
  };

  const confirmAction = () => {
    if (actionType === "withdraw") {
      dispatch(withdrawLeave(application._id));
    } else if (actionType === "cancel") {
      dispatch(cancelLeave(application._id));
    }
    setShowConfirm(false);
    setActionType(null);
  };

  const closePopup = () => {
    setShowConfirm(false);
    setActionType(null);
  };

  const leaveTypeDisplayMap: Record<string, string> = {
    paid: "Paid Leave",
    sick: "Sick Leave",
    casual: "Casual Leave",
    lossOfPay: "Loss of Pay",
    compOff: "Comp-Off Leave",
  };

  const getLeaveDisplayText = (application: any) => {
    if (application.applicationType === "compOffGrant") return "Comp-Off Grant";
    if (application.applicationType === "leave") {
      return (
        leaveTypeDisplayMap[application.category] ||
        `${application.category} Leave`
      );
    }
    return application.applicationType;
  };

  return (
    <div className="border border-gray-200 rounded-lg p-5 bg-white hover:shadow-md transition-shadow duration-200 flex flex-col justify-between">
      {/* Existing Card Layout */}
      <div>
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span
                className={`inline-block w-3 h-3 rounded-full ${
                  application.status === "approved"
                    ? "bg-green-400"
                    : application.status === "rejected"
                    ? "bg-red-400"
                    : "bg-yellow-400"
                }`}
              ></span>
              <h3 className="font-semibold text-gray-800">
                {getLeaveDisplayText(application)}
              </h3>
            </div>

            <div className="text-sm text-gray-600 mt-1">
              {application.applicationType === "leave" ? (
                <div className="flex flex-wrap gap-x-4">
                  <span>
                    <span className="font-medium">From:</span>{" "}
                    {application.fromDate} ({application.fromSession})
                  </span>
                  <span>
                    <span className="font-medium">To:</span>{" "}
                    {application.toDate} ({application.toSession})
                  </span>
                </div>
              ) : (
                <span>
                  <span className="font-medium">Earned on:</span>{" "}
                  {application.dateEarned}
                </span>
              )}
            </div>

            {application.reason && (
              <div className="mt-3">
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Reason:</span>{" "}
                  {application.reason}
                </p>
              </div>
            )}
          </div>

          <div className="ml-4 flex flex-col items-end">
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium ${
                application.status === "approved"
                  ? "bg-green-100 text-green-800"
                  : application.status === "rejected"
                  ? "bg-red-100 text-red-800"
                  : "bg-yellow-100 text-yellow-800"
              }`}
            >
              {application.status.charAt(0).toUpperCase() +
                application.status.slice(1)}
            </span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      {(isWithdrawVisible || isCancelVisible) && (
        <div className="flex justify-end space-x-3 mt-4 pt-3 border-t border-gray-100">
          {isWithdrawVisible && (
            <button
              onClick={handleWithdrawClick}
              className="px-4 py-2 bg-blue-400 text-white text-sm rounded-lg hover:bg-blue-500 transition shadow-sm"
            >
              Withdraw
            </button>
          )}
          {isCancelVisible && (
            <button
              onClick={handleCancelClick}
              className="px-4 py-2 bg-red-400 text-white text-sm rounded-lg hover:bg-red-500 transition shadow-sm"
            >
              Cancel Leave
            </button>
          )}
        </div>
      )}

      {/* Confirmation Popup */}
      {showConfirm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded-xl p-6 shadow-xl w-[90%] max-w-md">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              {actionType === "withdraw"
                ? "Confirm Withdrawal"
                : "Confirm Leave Cancel"}
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              {actionType === "withdraw"
                ? "Are you sure you want to withdraw this leave application?"
                : "Are you sure you want to cancel this approved leave application?"}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={closePopup}
                className="px-4 py-2 bg-gray-200 text-gray-800 text-sm rounded-lg hover:bg-gray-300"
              >
                Close
              </button>
              <button
                onClick={confirmAction}
                className="px-4 py-2 bg-red-400 text-white text-sm rounded-lg hover:bg-red-500"
              >
                {actionType === "withdraw"
                  ? "Yes, Withdraw"
                  : "Yes, Cancel Leave"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveCard;
